import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  getSelectStateClass,
  Invoice,
  mapStatusTracking,
  PriceType,
  Status,
  StatusTrackingOptions,
  TypeBill,
} from '@app/shared/interfaces/invoice.interface';
import { MESSAGES } from '@app/shared/utils/messages';
import { Pageable } from '@app/shared/interfaces/pagination.interface';
import { BaseTableComponent } from '@app/shared/components/organisms/table/base-table.component';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ButtonIcon, ButtonSize, ButtonStyle } from '@app/shared/components/atoms/buttons/button.type';
import { Style, Size as IconSize } from '@app/shared/components/atoms/icon/icon.component';
import { InvoicesService } from '@app/components/invoice/invoices.service';
import { lastValueFrom } from 'rxjs';
import { AlertService } from '@app/shared/components/organisms/alert/alert.service';
import {
  InvoicesGetEnum,
  InvoicesStatusEnum,
  mapInvoiceStatusToRequest,
  mapTypeBill,
  mapPriceType,
  mapPriceTypeToColor,
  mapTypeFailedEmailsToLibelle,
  ListInvoiceSchema,
} from '@app/components/invoice/invoices.model';
import { ConfirmDialogData } from '@app/shared/components/organisms/dialog/dialog.data';
import { DialogService } from '@app/shared/components/organisms/dialog/dialog.service';
import { Color } from '@app/shared/models/color.model';
import { SelectionModel } from '@angular/cdk/collections';
import { downloadCsv, extractParentheses, formatEmails, toCsv } from '@app/shared/utils/utils';
import { SettingsService } from '../settings/settings.service';
import { ToggleSwitchSize } from '@app/shared/models/toggle-switch.model';
import { compareByNumber, formatDateForFilter } from '@app/shared/utils/sort';
import { DateRangePickerComponent } from '@app/shared/components/atoms/date-range-picker/date-range-picker.component';
import { FilterItem } from '@app/shared/components/organisms/table/column-header/column-header.component';
import { TooltipPosition } from '@app/shared/components/atoms/icon/icon.component';
import { DateTime } from 'luxon';
import { WebsocketEventType, WebsocketService } from '@app/shared/websocket/websocket.service';

const DEFAULT_PAGE_SIZE: number = 10;

interface InvoicesRequestParams {
  status: string[];
  startDate?: string;
  endDate?: string;
  selectedFilter?: string;
}

type InvoiceArchivedCsvRow = {
  facture: string;
  producteur: string;
  site: string;
  prm: string;
  filiere: string;
  periodeFacturation: string;
  dateEmission: string;
  montantHt: string;
  volumeMWh: string;
  formule: string;
  type: string;
  suivi: string;
  contact: string;
  commentaire: string;
};

type InvoiceTodoCsvRow = {
  etat: string;
  dateGeneration: string;
  facture: string;
  producteur: string;
  site: string;
  prm: string[];
  filiere: string;
  periodeFacturation: string;
  montantHt: string;
  volumeMWh: string;
  formule: string;
  type: string;
  contact: string;
  commentaire: string;
};

const invoiceArchivedHeaders: { key: keyof InvoiceArchivedCsvRow; label: string }[] = [
  { key: 'facture', label: 'Facture' },
  { key: 'producteur', label: 'Producteur' },
  { key: 'site', label: 'Site' },
  { key: 'prm', label: 'PRM' },
  { key: 'filiere', label: 'Filière' },
  { key: 'periodeFacturation', label: 'Période de facturation' },
  { key: 'dateEmission', label: "Date d'émission" },
  { key: 'montantHt', label: 'Montant HT' },
  { key: 'volumeMWh', label: 'Volume MWh' },
  { key: 'formule', label: 'Formule' },
  { key: 'type', label: 'Type' },
  { key: 'suivi', label: 'Suivi' },
  { key: 'contact', label: 'Contact' },
  { key: 'commentaire', label: 'Commentaire' },
];

const invoiceTodoHeaders: { key: keyof InvoiceTodoCsvRow; label: string }[] = [
  { key: 'etat', label: 'État' },
  { key: 'dateGeneration', label: 'Date de génération' },
  { key: 'facture', label: 'Facture' },
  { key: 'producteur', label: 'Producteur' },
  { key: 'site', label: 'Site' },
  { key: 'prm', label: 'PRM' },
  { key: 'filiere', label: 'Filière' },
  { key: 'periodeFacturation', label: 'Période de facturation' },
  { key: 'montantHt', label: 'Montant HT' },
  { key: 'volumeMWh', label: 'Volume MWh' },
  { key: 'formule', label: 'Formule' },
  { key: 'type', label: 'Type' },
  { key: 'contact', label: 'Contact' },
  { key: 'commentaire', label: 'Commentaire' },
];

@Component({
  selector: 'app-invoices',
  templateUrl: './invoices.component.html',
  styleUrls: ['./invoices.component.scss'],
  standalone: false,
})
export class InvoicesComponent implements OnInit, OnDestroy {
  @ViewChild(DateRangePickerComponent) dateRangePickerComponent!: DateRangePickerComponent;

  ToggleSwitchSize = ToggleSwitchSize;

  invoicesSource: Invoice[] = [];

  fieldSortDifferently = ['documentId', 'RES_CT.montant'];

  invoicesSourceSearched: Invoice[] = [];

  risSelection = new SelectionModel<string>(true, []);

  isLoading: boolean = true;

  totalInvoiceNumber: number = 0;

  threshold: number = 0;

  quota: number = 0;

  rangeStartDate: string = '';

  rangeEndDate: string = '';

  downloadLoaders: boolean[] = [];

  loaderSending: boolean = false;

  sendingLoaders: boolean[] = [];

  _pageable: Pageable<Invoice> = {
    content: [],
    total: 0,
    limit: 10,
    offset: 0,
  };

  currentPage: number = 0;

  currentPageSize: number = 10;

  invoicesNumberForSend: number = 0;

  invoicesValidateNumber: number = 0;

  warnings: Map<number, boolean> | undefined = new Map<number, boolean>();

  invoicesStatus: InvoicesStatusEnum = InvoicesStatusEnum.EN_COURS;

  protected readonly InvoicesStatusEnum = InvoicesStatusEnum;

  noDataMessage: string = MESSAGES.NO_DATA_ERROR;

  protected readonly TooltipPosition = TooltipPosition;

  protected readonly IconSize = IconSize;

  protected readonly ButtonIcon = ButtonIcon;

  protected readonly ButtonSize = ButtonSize;

  protected readonly Style = Style;

  protected readonly Object = Object;

  protected readonly Status = Status;

  protected readonly Color = Color;

  protected readonly ButtonStyle = ButtonStyle;

  protected readonly extractParentheses = extractParentheses;

  protected readonly formatEmails = formatEmails;

  protected readonly statusTrackingOptions = StatusTrackingOptions;

  protected readonly mapStatusTracking = mapStatusTracking;

  protected readonly getSelectStateClass = getSelectStateClass;

  protected readonly TypeBill = TypeBill;

  protected readonly PriceType = PriceType;

  protected readonly mapTypeBill = mapTypeBill;

  protected readonly mapPriceType = mapPriceType;

  protected readonly mapPriceTypeToColor = mapPriceTypeToColor;

  protected filterItems: { [key: string]: FilterItem[] } = {};

  hoveredTooltipId: number | null = null;

  numberInvoiceError: number | null = null;

  public searchTerm: string = '';

  selectedFilter = '';

  filterOptions() {
    const defaultOptions = [
      { value: 'dateGenerated', label: 'Date de génération' },
      { value: 'DateDebutCalcul', label: 'Période de facturation' },
    ];

    if (this.invoicesStatus === InvoicesStatusEnum.ERROR) {
      return defaultOptions;
    }

    if (this.invoicesStatus === InvoicesStatusEnum.ARCHIVE) {
      return [
        { value: 'sentDate', label: "Date d'émission" },
        { value: 'DateDebutCalcul', label: 'Période de facturation' },
      ];
    }
    return defaultOptions;
  }

  protected readonly downloadStatus: Status[] = [Status.GENERATED, Status.SENT, Status.VALIDATED, Status.REFUSED];

  @ViewChild(BaseTableComponent, { static: false })
  private baseTable!: BaseTableComponent<Invoice>;

  constructor(
    private dialogService: DialogService,
    private router: Router,
    private route: ActivatedRoute,
    private invoicesService: InvoicesService,
    private alertService: AlertService,
    private settingService: SettingsService,
    private websocketService: WebsocketService,
  ) {}

  ngOnDestroy(): void {
    this.invoicesSource = [];
    this.invoicesSourceSearched = [];
  }

  ngOnInit() {
    this.route.queryParams.subscribe((params: Params) => {
      this.currentPage = +(params.page ?? 0);
      this.currentPageSize = +(params.pageSize ?? DEFAULT_PAGE_SIZE);
      this.getInvoiceData();
    });

    this.listenInvoicesUpdated();
  }

  listenInvoicesUpdated(): void {
    this.websocketService
      .onInvoicesUpdated()
      .pipe()
      .subscribe(({ newItem, oldItem, type }) => {
        this.updateSingleInvoiceRow(newItem as Invoice, oldItem as Invoice, type);
      });
  }

  async getInvoiceData() {
    this.isLoading = true;
    this.invoicesSource = [];
    this.invoicesSourceSearched = [];
    await this.getInvoices();
    this.isLoading = false;
  }

  async getInvoices(): Promise<void> {
    await this.getInvoicesRequest({ status: [InvoicesGetEnum.IN_PROGRESS, InvoicesGetEnum.ERROR] });
  }

  triggerSearch() {
    this.getInvoicesSearch(this.searchTerm.trim());
  }

  getInvoicesSearch(searchInput: string): void {
    this.isLoading = true;

    if (searchInput === '') {
      this.invoicesSourceSearched = this.invoicesSource;
    } else {
      this.invoicesSourceSearched = this.invoicesSource.filter((item) => this.matches(item, searchInput));
    }

    this.baseTable?.resetPage();
    this.isLoading = false;
  }

  matches(item: Invoice, searchText: string): boolean {
    const lowerSearchValue = searchText.toLowerCase();

    const fields = [item.producer?.name, item.site?.name, item.allPrms, item.number, item.documentId];

    return fields.some((field) => field?.toString().toLowerCase().includes(lowerSearchValue));
  }

  private async getInvoicesRequest(params: InvoicesRequestParams) {
    const { currentPageSize: pageSize, currentPage: page } = this;

    this.warnings = new Map<number, boolean>();
    const offset = page * pageSize;
    try {
      const queryParams: { [key: string]: string } = {
        status: params.status.join(','),
      };
      if (params.startDate) {
        queryParams['startDate'] = params.startDate;
      }
      if (params.endDate) {
        queryParams['endDate'] = params.endDate;
      }
      if (params.selectedFilter) {
        queryParams['selectedFilter'] = params.selectedFilter;
      }

      const response = await lastValueFrom(this.invoicesService.getInvoices(queryParams));
      const isArchive = params.status.includes(InvoicesGetEnum.SENT);
      if (isArchive) {
        const result = ListInvoiceSchema.safeParse(response.list);
        if (!result.success) {
          console.error('Réponse API invalide :', result.error);
        }
      }

      this.numberInvoiceError = response.list_error.Items.length;

      const pageable: Pageable<Invoice> = {
        total: response.list.Items.length,
        limit: pageSize,
        offset,
        content: response.list.Items,
      };

      const { content, total } = pageable;
      let sortedContent = [...content];

      if (isArchive) {
        sortedContent = sortedContent.sort().map((item) => ({
          ...item,
          emails: this.getEmails(item),
        }));
      }

      this.pageable = pageable;
      this.invoicesSource = isArchive ? sortedContent : content;
      this.invoicesSourceSearched = isArchive ? sortedContent : content;
      this.totalInvoiceNumber = total;

      if (this.searchTerm?.trim()) {
        this.getInvoicesSearch(this.searchTerm.trim());
      }

      const invoiceFiltredHeaderList = ['typeBill', 'formulaLabel', 'tracking'];
      this.generateFilterItems(invoiceFiltredHeaderList as (keyof Invoice)[]);

      this.setValidatedInvoiceNumber();
      this.onInvoiceRequest();
    } catch (error) {
      console.error(`Erreur lors de la récupération des factures au statut : ${params.status}`, error);
      this.isLoading = false;
    }
  }

  get pageable() {
    return { ...this._pageable };
  }

  set pageable(pageable: Pageable<Invoice>) {
    this._pageable = pageable;
  }

  onPageableChange(pageable: Pageable<Invoice>) {
    if (!pageable || !pageable.offset || !pageable.limit) {
      return;
    }

    const { offset, limit: pageSize } = pageable;

    if (JSON.stringify(pageable) === JSON.stringify(this.pageable)) return;
    const page = offset / pageSize;

    const queryParams: { page: number | null; pageSize: number | null } = {
      page: null,
      pageSize: null,
    };

    queryParams.page = page === 0 ? null : page;
    queryParams.pageSize = pageSize === DEFAULT_PAGE_SIZE ? null : pageSize;

    this.addQueryParams(queryParams);
  }

  private addQueryParams(queryParams?: Params) {
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
      })
      .then();
  }

  masterPageToggle(select: boolean) {
    const invoicesSelectable = this.baseTable.displayableRows.filter((data) => this.isSelectable(data));
    const invoiceIds = invoicesSelectable.map(({ timestamp }) => timestamp);
    if (!select) {
      this.risSelection.deselect(...invoiceIds);
    } else {
      this.risSelection.select(...invoiceIds);
    }
  }

  onInvoiceRequest() {
    this.settingService.getSettings().subscribe({
      next: (response) => {
        this.quota = response.quota ?? 0;
        this.threshold = response.threshold ?? 0;
        this.invoicesNumberForSend = Math.ceil((this.quota * this.totalInvoiceNumber) / 100) || 1;
      },
      error: () => {
        this.alertService.error(MESSAGES.DEFAULT_ERROR);
      },
    });
  }

  checkboxLabel(selectionId?: string): string {
    if (!selectionId) {
      return `${this.isAllPageSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.risSelection.isSelected(selectionId) ? 'deselect' : 'select'} row ${selectionId + 1}`;
  }

  isAllPageSelected() {
    const numSelected = this.invoicesSourceSearched.filter(({ timestamp }) =>
      this.risSelection.selected.find((s) => s === timestamp),
    );
    const numRows = this.invoicesSourceSearched.length;
    return numSelected.length === numRows;
  }

  isCurrentPageSelected() {
    const numSelected = this.invoicesSourceSearched.filter(({ timestamp }) =>
      this.risSelection.selected.find((s) => s === timestamp),
    );
    const numRows = this.invoicesSourceSearched.length;
    return numSelected.length > 0 && numSelected.length !== numRows;
  }

  async onSelectStatusChange(row: Invoice, selectedValue: string): Promise<void> {
    const updatedInvoice: Invoice = {
      ...row,
      tracking: selectedValue,
    };

    const updateTracking: Partial<Invoice> = { prm: row.prm, timestamp: row.timestamp, tracking: selectedValue };

    this.invoicesService.putInvoices(updateTracking).subscribe({
      next: () => {
        const indexSearched = this.invoicesSourceSearched.findIndex((inv) => inv.timestamp === row.timestamp);
        this.invoicesSourceSearched[indexSearched] = updatedInvoice;

        const indexSource = this.invoicesSource.findIndex((inv) => inv.timestamp === row.timestamp);
        this.invoicesSource[indexSource] = updatedInvoice;

        const invoiceFiltredHeaderList = ['typeBill', 'formulaLabel', 'tracking'];
        this.generateFilterItems(invoiceFiltredHeaderList as (keyof Invoice)[]);
      },
      error: (err) => console.error('Erreur lors de la mise à jour', err),
    });
  }

  downloadInvoice(event: Event, invoice: Invoice, index: number) {
    this.downloadLoaders[index] = true;
    const documentId = invoice.documentId!;
    const documentSite = invoice.site.name.replace(/[\/\\:*?"<>|]/g, '_').replace(/\s+/g, '_');
    const downloadName = `${documentId.replace('-B', '')}-${documentSite}.pdf`;

    this.invoicesService.downloadInvoice(documentId).subscribe({
      next: (response: { data: string }) => {
        const a = document.createElement('a');
        a.href = `data:application/pdf;base64,${response.data}`;
        a.download = downloadName;

        a.click();
        this.downloadLoaders[index] = false;
      },
      error: () => {
        this.alertService.error(MESSAGES.DOWNLOAD_ERROR);
        this.downloadLoaders[index] = false;
      },
    });
  }

  /**
   * Méthode récupérant le type de validation choisi dans le toggle
   * @param validationType Le type de validation
   * @param data la facture
   */
  setSelectedValidation(validationType: string, data: Invoice) {
    const invoiceIndex = this.invoicesSourceSearched.findIndex((invoice) => invoice.documentId === data.documentId);
    if (this.invoicesSourceSearched[invoiceIndex].status === validationType) return;
    if (validationType === Status.VALIDATED && this.isValidPrice(this.invoicesSourceSearched[invoiceIndex])) {
      this.showConfirmValidationDialog(invoiceIndex);
      return;
    }
    this.putInvoices(invoiceIndex, validationType as Status);
  }

  /**
   * Méthode permettant de mettre a jour la facture en base de donnée
   * @param i L'index de la facture
   * @param status Le status à mettre en bdd
   */
  private putInvoices(i: number, status: Status) {
    this.invoicesService.putInvoices(this.getObjectInvoice(i, { status })).subscribe({
      next: () => {
        this.invoicesSourceSearched[i].status = status;
        this.setValidatedInvoiceNumber();
      },
      error: () => this.alertService.error(MESSAGES.STATUS_ERROR),
    });
  }

  /**
   * Méthode permettant de récupérer l'objet pour le put invoice
   * @param i L'index de la facture
   * @param properties paramètre de la facture
   * @private
   */
  private getObjectInvoice(i: number, properties: Partial<Invoice>): Partial<Invoice> {
    return {
      ...properties,
      prm: this.invoicesSourceSearched[i]?.prm,
      timestamp: this.invoicesSourceSearched[i]?.timestamp,
    };
  }

  /**
   * Méthode pour le rafraichissement des factures dans le tableau
   */
  async refreshInvoices() {
    this.invoicesSource = [];
    this.invoicesSourceSearched = [];
    this.isLoading = true;

    this.rangeStartDate = '';
    this.rangeEndDate = '';
    this.selectedFilter = '';

    const status: InvoicesGetEnum[] = [InvoicesGetEnum.ERROR];
    let startDate: string | undefined;
    let endDate: string | undefined;
    let selectedFilter: string | undefined;

    switch (this.invoicesStatus) {
      case InvoicesStatusEnum.EN_COURS:
        status.push(InvoicesGetEnum.IN_PROGRESS);
        break;

      case InvoicesStatusEnum.ARCHIVE:
        status.push(InvoicesGetEnum.SENT);
        startDate = DateTime.now().minus({ months: 4 }).toISODate();
        endDate = DateTime.now().toISODate();
        selectedFilter = 'sentDate';
        this.selectedFilter = selectedFilter;
        this.rangeStartDate = startDate;
        this.rangeEndDate = endDate;
        break;
    }

    await this.getInvoicesRequest({ status, startDate, endDate, selectedFilter });
    this.isLoading = false;
  }

  setValidatedInvoiceNumber() {
    this.invoicesValidateNumber = this.invoicesSourceSearched.filter(
      (invoice) => invoice.status === Status.VALIDATED,
    ).length;
  }

  isSendable() {
    return this.invoicesValidateNumber >= this.invoicesNumberForSend;
  }

  /**
   * Méthode pour l'envoi en masse des mails
   */
  sendAllMail() {
    const invoicesToSend = this.invoicesSourceSearched.filter(
      (invoice) => Status.VALIDATED === invoice.status || Status.GENERATED === invoice.status,
    );
    this.sendMail(invoicesToSend);
  }

  /**
   * Méthode pour l'envoi d'un mail
   */
  sendOneMail(i: number) {
    this.sendingLoaders[i] = true;
    this.sendMail([this.invoicesSourceSearched[i]]);
    this.sendingLoaders[i] = false;
  }

  /**
   * Méthode pour l'envoi en masse des mails
   */
  sendMail(invoicesToSend: Invoice[]) {
    this.invoicesService.sendInvoices(invoicesToSend).subscribe({
      next: () => {
        this.alertService.success('Factures envoyées avec succès');
        this.refreshInvoices();
        this.loaderSending = false;
      },
      error: () => {
        this.alertService.error(MESSAGES.SEND_MAIL_ERROR);
        this.loaderSending = false;
      },
    });
  }

  /**
   * Méthode permettant de mettre à jour les factures en fonction de la valeur selectionnée (en cours ou archivé)
   *
   * @param status Le nouveau statut choisi entre en cours ou archivé
   */
  async invoiceStatus(status: string) {
    this.isLoading = true;
    this.invoicesStatus = status as InvoicesStatusEnum;
    this.rangeStartDate = '';
    this.rangeEndDate = '';
    this.selectedFilter = '';

    if (this.invoicesStatus === InvoicesStatusEnum.ERROR) {
      this.searchTerm = '';
    }

    const statusMapping = {
      [InvoicesStatusEnum.ARCHIVE]: InvoicesGetEnum.SENT,
      [InvoicesStatusEnum.EN_COURS]: InvoicesGetEnum.IN_PROGRESS,
      [InvoicesStatusEnum.ERROR]: InvoicesGetEnum.ERROR,
    };

    const mappedStatus = statusMapping[this.invoicesStatus];
    const adaptedStatus = [mappedStatus];
    if (mappedStatus !== InvoicesGetEnum.ERROR) {
      adaptedStatus[1] = InvoicesGetEnum.ERROR;
    }
    if (mappedStatus === InvoicesGetEnum.SENT) {
      this.rangeStartDate = DateTime.now().minus({ months: 4 }).toISODate();
      this.rangeEndDate = DateTime.now().toISODate();
      this.selectedFilter = 'sentDate';
    }

    if (mappedStatus !== undefined) {
      await this.getInvoicesRequest({
        status: adaptedStatus,
        startDate: this.rangeStartDate,
        endDate: this.rangeEndDate,
        selectedFilter: this.selectedFilter,
      });
    }

    this.risSelection.clear();

    this.isLoading = false;
  }

  /**
   * Méthode permettant l'ouverture de la pop-up avec les bons paramètres
   * @param data la facture
   */
  showCommentDialog(data: Invoice) {
    const options: ConfirmDialogData = {
      textArea: data.comment ? data.comment : '',
      title: 'Ajouter un commentaire',
      confirmText: 'Enregistrer',
      placeholder: 'Veuillez saisir votre commentaire',
      cancelText: 'Annuler',
      enableConfirmButton: true,
      width: '50rem',
      readOnly: false,
      confirmFunction: (value) => {
        const updateInvoice: Partial<Invoice> = {
          prm: data.prm,
          timestamp: data.timestamp,
          comment: value,
        };
        this.invoicesService.putInvoices(updateInvoice).subscribe({
          next: () => {
            this.invoicesSourceSearched = this.invoicesSourceSearched.map((inv) =>
              inv.timestamp === data.timestamp ? { ...inv, comment: value } : inv,
            );

            this.invoicesSource = this.invoicesSource.map((inv) =>
              inv.timestamp === data.timestamp ? { ...inv, comment: value } : inv,
            );
          },
          error: () => {},
        });
      },
    };
    this.dialogService.open(options);
  }

  showConfirmValidationDialog(i: number) {
    const data = this.invoicesSourceSearched[i!];
    const options: ConfirmDialogData = {
      placeholder: `La facture ${data.documentId} est en dessous du seuil de ${this.threshold} euros. Voulez-vous valider la facture avant de l'envoyer ?`,
      title: 'Confirmer la validation',
      confirmText: 'Oui',
      cancelText: 'Non',
      enableConfirmButton: true,
      width: '50rem',
      readOnly: true,
      confirmFunction: () => {
        this.putInvoices(i, Status.VALIDATED);
      },
    };
    this.dialogService.open(options);
  }

  showConfirmDeleteDialog() {
    const options: ConfirmDialogData = {
      placeholder:
        this.risSelection.selected.length === 1
          ? `Voulez-vous supprimer la facture sélectionnée ?`
          : `Voulez-vous supprimer les ${this.risSelection.selected.length} factures sélectionnées ?`,
      title: 'Confirmer la suppression',
      confirmText: 'Oui',
      cancelText: 'Non',
      enableConfirmButton: true,
      width: '50rem',
      readOnly: true,
      confirmFunction: () => {
        this.deleteInvoices();
      },
    };
    this.dialogService.open(options);
  }

  isStatus(data: unknown, status: Status) {
    return (data as Invoice)?.status === status;
  }

  isValidPrice(data: unknown) {
    const resCt = (data as Invoice)?.RES_CT;
    return resCt?.montant && parseInt(resCt.montant, 10) < this.threshold;
  }

  isCheckable(data: unknown) {
    const status = (data as Invoice)?.status;
    return Status.GENERATED === status || Status.VALIDATED === status || Status.REFUSED === status;
  }

  isSelectable(data: Invoice) {
    return (
      this.invoicesStatus === InvoicesStatusEnum.EN_COURS ||
      (this.invoicesStatus === InvoicesStatusEnum.ARCHIVE && data.typeBill === TypeBill.DRAFT)
    );
  }

  /**
   * Vérifie si le statut est généré, validé ou refusé.
   * @param status Le statut à vérifier.
   * @returns true si le statut est généré, validé ou refusé, sinon false.
   */
  isStatusGeneratedValidatedOrRefused(status: Status): boolean {
    return [Status.GENERATED, Status.VALIDATED, Status.REFUSED].includes(status);
  }

  async onStartDateChanged(newStartDate: string) {
    this.rangeStartDate = formatDateForFilter(newStartDate, 'INF');
    const endDate = formatDateForFilter(this.rangeEndDate || DateTime.now().toISODate(), 'SUP');
    this.isLoading = true;

    await this.getInvoicesRequest({
      status: mapInvoiceStatusToRequest(this.invoicesStatus),
      startDate: this.rangeStartDate,
      endDate,
      selectedFilter: this.selectedFilter,
    });

    this.baseTable?.resetPage();
    this.isLoading = false;
  }

  async onEndDateChanged(newEndDate: string) {
    this.rangeEndDate = formatDateForFilter(newEndDate, 'SUP');
    const startDate = formatDateForFilter(this.rangeStartDate, 'INF');

    this.isLoading = true;

    await this.getInvoicesRequest({
      status: mapInvoiceStatusToRequest(this.invoicesStatus),
      startDate,
      endDate: this.rangeEndDate,
      selectedFilter: this.selectedFilter,
    });

    this.baseTable?.resetPage();
    this.isLoading = false;
  }

  deleteInvoices() {
    const deleteList = this.risSelection.selected.map((timestamp) => {
      const matchedInvoice = this.invoicesSourceSearched.find((invoice) => invoice.timestamp === timestamp);
      return matchedInvoice ? matchedInvoice : { timestamp, prm: undefined };
    });
    this.invoicesService.deleteInvoices(deleteList).subscribe({
      next: (response) => {
        const errorDelete = response.filter((item) => item.status === 'Error');
        if (errorDelete.length > 0) {
          this.alertService.error(
            `Erreur lors de la suppresion des factures : ${errorDelete.map((item) => item.id).join(', ')}`,
          );
        } else {
          this.alertService.success('Factures supprimées avec succès');
        }
        this.refreshInvoices();
        this.risSelection.clear();
      },
      error: (err) => {
        console.log(err);
        this.alertService.error(MESSAGES.DELETE_ERROR);
      },
    });
  }

  getInvoiceId(data: Invoice) {
    if (data.typeBill === 'DRAFT' || data.status === 'GENERATING') {
      return '-';
    }
    if (this.invoicesStatus === InvoicesStatusEnum.EN_COURS) {
      return data.documentId?.replace('-B', '');
    }
    return data.documentId;
  }

  getEmails(data: Invoice) {
    return data.contacts?.map((contact) => contact.email).join(', ') || '-';
  }

  getInvoicePeriod(start: string, end: string) {
    const format = 'dd/MM/yyyy';
    const startDate = DateTime.fromISO(start).toFormat(format);
    const endDate = DateTime.fromISO(end).toFormat(format);
    return `${startDate} - ${endDate}`;
  }

  comparePrefixedStrings(a: string, b: string): number {
    const regex = /^(SOR_)?([A-Z]+_)(\d+)$/;

    const matchA = a.match(regex);
    const matchB = b.match(regex);

    if (!matchA && !matchB) return a.localeCompare(b);
    if (!matchA) return -1; // first '-'
    if (!matchB) return 1;

    const [, , prefixA, numA] = matchA;
    const [, , prefixB, numB] = matchB;

    const prefixCompare = prefixA.localeCompare(prefixB);
    if (prefixCompare !== 0) return prefixCompare;

    return Number(numA) - Number(numB);
  }

  generateFilterItems = (headerList: (keyof Invoice)[]) => {
    const setsMap = new Map<keyof Invoice, Set<string>>();
    this.filterItems = {};

    headerList.forEach((field) => {
      setsMap.set(field, new Set<string>());
      this.filterItems[field] = [];
    });

    this.invoicesSource.forEach((item) => {
      headerList.forEach((field) => {
        const fieldValue = item[field] as string;

        if (fieldValue && !setsMap.get(field)?.has(fieldValue)) {
          setsMap.get(field)?.add(fieldValue);
          this.filterItems[field].push({ name: fieldValue, checked: false });
        }
      });
    });
  };

  exportInvoices() {
    if (this.invoicesStatus === InvoicesStatusEnum.ARCHIVE) {
      this.exportArchivedInvoices();
    } else {
      this.exportTodoInvoices();
    }
  }

  private exportArchivedInvoices() {
    const rows: InvoiceArchivedCsvRow[] = this.invoicesSourceSearched.map((invoice) => ({
      facture: this.getInvoiceId(invoice) || '-',
      producteur: invoice.producer.name,
      site: invoice.site.name,
      prm: invoice.prm,
      filiere: invoice.site.technology,
      periodeFacturation: this.getInvoicePeriod(invoice.DateDebutCalcul, invoice.DateFinCalcul),
      dateEmission: DateTime.fromFormat(invoice.sentDate, 'yyyy-MM-dd HH:mm').toFormat('dd/MM/yyyy HH:mm'),
      montantHt: invoice.RES_CT?.montant || '-',
      volumeMWh: invoice.quantity || '-',
      formule: extractParentheses(invoice.formulaLabel),
      type: mapTypeBill(invoice.typeBill),
      suivi: invoice.tracking || '-',
      contact: this.getEmails(invoice),
      commentaire: invoice.comment || '-',
    }));

    downloadCsv(
      `billing_factures_archivees_${DateTime.now().toFormat('yyyyLLdd')}.csv`,
      toCsv(rows, invoiceArchivedHeaders),
    );
  }

  private exportTodoInvoices() {
    const rows: InvoiceTodoCsvRow[] = this.invoicesSourceSearched.map((invoice) => ({
      etat: invoice.status,
      dateGeneration: DateTime.fromFormat(invoice.dateGenerated, 'yyyy-MM-dd HH:mm').toFormat('dd/MM/yyyy HH:mm'),
      facture: this.getInvoiceId(invoice) || '-',
      producteur: invoice.producer.name,
      site: invoice.site.name,
      prm: invoice.allPrms,
      filiere: invoice.site.technology,
      periodeFacturation: this.getInvoicePeriod(invoice.DateDebutCalcul, invoice.DateFinCalcul),
      montantHt: invoice.RES_CT?.montant || '-',
      volumeMWh: invoice.quantity || '-',
      formule: extractParentheses(invoice.formulaLabel),
      type: mapTypeBill(invoice.typeBill),
      contact: this.getEmails(invoice),
      commentaire: invoice.comment || '-',
    }));

    downloadCsv(
      `billing_factures_en_cours_${DateTime.now().toFormat('yyyyLLdd')}.csv`,
      toCsv(rows, invoiceTodoHeaders),
    );
  }

  customSortFunction = (data: Invoice[], sort: 'asc' | 'desc' | undefined, field?: unknown): void => {
    if (typeof field !== 'string') return;

    data.sort((a, b) => {
      // eslint-disable @typescript-eslint/no-explicit-any
      const aValue = (a as any)[field];
      const bValue = (b as any)[field];
      // eslint-enable @typescript-eslint/no-explicit-any

      if (field === 'documentId') {
        return this.comparePrefixedStrings(aValue, bValue);
      }

      if (field === 'RES_CT.montant') {
        if (a.RES_CT?.montant && b.RES_CT?.montant) return compareByNumber(a.RES_CT?.montant, b.RES_CT?.montant);
      }

      return aValue > bValue ? 1 : -1;
    });

    if (sort === 'desc') {
      data.reverse();
    }
  };

  updateSingleInvoiceRow(newItem: Invoice | null, oldItem: Invoice | null, type: WebsocketEventType): void {
    switch (type) {
      case WebsocketEventType.INVOICE_DELETED:
      case WebsocketEventType.INVOICE_SENT:
        this.invoicesSource = this.invoicesSource.filter(
          (inv) => !(inv.prm === oldItem?.prm && inv.timestamp === oldItem?.timestamp),
        );
        this.invoicesSourceSearched = this.invoicesSourceSearched.filter(
          (inv) => !(inv.prm === oldItem?.prm && inv.timestamp === oldItem?.timestamp),
        );
        this.invoicesNumberForSend--;
        this.setValidatedInvoiceNumber();
        break;

      case WebsocketEventType.INVOICES_UPDATED:
        if (!newItem) return;
        const srcIndex = this.invoicesSource.findIndex(
          (inv) => inv.prm === oldItem?.prm && inv.timestamp === oldItem?.timestamp,
        );
        if (srcIndex !== -1) {
          this.invoicesSource = this.invoicesSource.map((inv, i) => (i === srcIndex ? { ...inv, ...newItem } : inv));

          const searchedIndex = this.invoicesSourceSearched.findIndex(
            (inv) => inv.prm === oldItem?.prm && inv.timestamp === oldItem?.timestamp,
          );
          if (searchedIndex !== -1) {
            this.invoicesSourceSearched = this.invoicesSourceSearched.map((inv, i) =>
              i === searchedIndex ? { ...inv, ...newItem } : inv,
            );
          }
          return;
        }
        break;

      case WebsocketEventType.INVOICE_ADDED:
        if (!newItem) return;
        this.invoicesSource = [newItem, ...this.invoicesSource];
        this.invoicesSourceSearched = [newItem, ...this.invoicesSourceSearched];
        this.invoicesNumberForSend++;
        break;
    }
  }

  openSendDialog(i: number) {
    const options: ConfirmDialogData = {
      title: "Confimer l'envoi",
      placeholder: 'Voulez-vous envoyer la facture archivée ?',
      readOnly: true,
      cancelText: 'Non',
      confirmText: 'Oui',
      enableConfirmButton: true,
      width: '50rem',
      confirmFunction: () => {
        this.sendOneMail(i);
      },
    };
    this.dialogService.open(options);
  }

  showTooltip(id: number) {
    this.hoveredTooltipId = id;
  }

  hideTooltip() {
    this.hoveredTooltipId = null;
  }

  async onFilterChanged(newFilter: string) {
    this.selectedFilter = newFilter;

    let startDate = '';
    let endDate = '';

    if (!newFilter) {
      this.rangeStartDate = '';
      this.rangeEndDate = '';
    } else if (this.rangeStartDate && this.rangeEndDate) {
      startDate = formatDateForFilter(this.rangeStartDate, 'INF');
      endDate = formatDateForFilter(this.rangeEndDate, 'SUP');
    }

    this.isLoading = true;

    try {
      await this.getInvoicesRequest({
        status: mapInvoiceStatusToRequest(this.invoicesStatus),
        startDate,
        endDate,
        selectedFilter: this.selectedFilter,
      });
      this.baseTable?.resetPage();
    } finally {
      this.isLoading = false;
    }
  }

  protected readonly mapTypeFailedEmailsToLibelle = mapTypeFailedEmailsToLibelle;
}
